<template>
  <div class="client-hierarchy qh-client-page">
    <!-- Hero Header / Jerarquía Visual -->
    <article class="qh-panel hero-panel">
      <div class="qh-panel-header hero-header">
        <div class="hero-copy">
          <div class="section-chip"><i class="bi bi-diagram-3-fill"></i> Estructura jerárquica</div>
          <h2 class="hero-title">Proyecto y canal</h2>
          <p class="hero-subtitle">Configura una estructura de dos niveles para organizar la operación del cliente y mantener el orden de la navegación.</p>
        </div>
        <div class="hero-flow" aria-label="Jerarquía de dos niveles">
          <div class="flow-card">
            <span class="flow-level">Nivel 1</span>
            <strong class="flow-name">Proyecto</strong>
            <small class="flow-desc">Orden + Descripción</small>
          </div>
          <div class="flow-connector">
            <i class="bi bi-arrow-right flow-arrow"></i>
          </div>
          <div class="flow-card secondary">
            <span class="flow-level">Nivel 2</span>
            <strong class="flow-name">Canal</strong>
            <small class="flow-desc">Orden + Descripción</small>
          </div>
        </div>
      </div>
    </article>

    <!-- Formularios de Creación (Fila de 2 Tarjetas) -->
    <div class="workspace-grid">
      <!-- Tarjeta: Crear Proyecto -->
      <article class="qh-panel workspace-card">
        <div class="qh-panel-header section-header">
          <div class="section-icon"><i class="bi bi-folder-plus"></i></div>
          <div>
            <h3 class="section-title">{{ proyectoEditando ? 'Editar proyecto' : 'Crear proyecto' }}</h3>
            <p class="section-text">Primer nivel de clasificación para agrupar canales relacionados.</p>
          </div>
        </div>
        <div class="qh-panel-body">
          <form class="workspace-form" @submit.prevent="guardarProyecto">
            <div class="row g-3">
                            <div class="col-12 col-sm-4">
                <label for="proyectoOrden" class="qh-label">
                  <i class="bi bi-sort-numeric-down"></i> Orden
                  <span v-if="proyectoEditando" class="text-muted fw-normal small ms-1">(Bloqueado)</span>
                </label>
                <input
                  id="proyectoOrden"
                  v-model.number="proyectoForm.orden"
                  type="number"
                  min="0"
                  class="form-control"
                  placeholder="Ej. 1"
                  :disabled="!!proyectoEditando"
                  :title="proyectoEditando ? 'El nÃºmero de orden no se puede modificar' : ''"
                  required
                >
              </div>
              <div class="col-12 col-sm-8">
                <label for="proyectoDescripcion" class="qh-label">
                  <i class="bi bi-card-text"></i> Descripción
                </label>
                <input id="proyectoDescripcion" v-model.trim="proyectoForm.descripcion" maxlength="150" class="form-control" placeholder="Ej. Clorox Moderno" required>
              </div>
            </div>

            <div class="form-actions-row">
              <button v-if="proyectoEditando" class="btn qh-btn qh-btn-secondary" type="button" @click="cancelarProyecto">
                <i class="bi bi-x-circle"></i> Cancelar
              </button>
              <button class="btn qh-btn qh-btn-primary" type="submit" :disabled="guardandoProyecto || !proyectoValido">
                <span v-if="guardandoProyecto" class="spinner-border spinner-border-sm"></span>
                <i v-else class="bi bi-check2-circle"></i>
                <span>{{ proyectoEditando ? 'Actualizar proyecto' : 'Guardar proyecto' }}</span>
              </button>
            </div>
          </form>
        </div>
      </article>

      <!-- Tarjeta: Crear Canal -->
      <article class="qh-panel workspace-card">
        <div class="qh-panel-header section-header">
          <div class="section-icon"><i class="bi bi-signpost-split"></i></div>
          <div>
            <h3 class="section-title">{{ canalEditando ? 'Editar canal' : 'Crear canal' }}</h3>
            <p class="section-text">Segundo nivel asociado a un proyecto ya registrado.</p>
          </div>
        </div>
        <div class="qh-panel-body">
          <form class="workspace-form" @submit.prevent="guardarCanal">
            <div class="row g-3">
              <div class="col-12 col-sm-4">
                <label for="canalOrden" class="qh-label">
                  <i class="bi bi-sort-numeric-down"></i> Orden
                  <span v-if="canalEditando" class="text-muted fw-normal small ms-1">(Bloqueado)</span>
                </label>
                <input
                  id="canalOrden"
                  v-model.number="canalForm.orden"
                  type="number"
                  min="0"
                  class="form-control"
                  placeholder="Ej. 1"
                  :disabled="!!canalEditando"
                  :title="canalEditando ? 'El número de orden no se puede modificar' : ''"
                  required
                >
              </div>
              <div class="col-12 col-sm-8 autocomplete-field">
                <label for="canalProyecto" class="qh-label">
                  <i class="bi bi-folder2-open"></i> Proyecto asociado
                </label>
                <div v-if="proyectos.length > 4" class="position-relative">
                  <div class="input-group">
                    <input
                      id="canalProyecto"
                      v-model="busquedaProyectoCanal"
                      type="text"
                      class="form-control"
                      placeholder="Buscar proyecto por orden o nombre..."
                      @focus="mostrarListaProyectosCanal = true"
                      @input="mostrarListaProyectosCanal = true"
                      @blur="ocultarListaProyectosCanal"
                      required
                    />
                    <button v-if="canalForm.idProyecto || busquedaProyectoCanal" type="button" class="btn btn-outline-secondary" @click="limpiarProyectoCanal">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                  <ul v-if="mostrarListaProyectosCanal && proyectosParaCanalFiltrados.length" class="autocomplete-list">
                    <li v-for="p in proyectosParaCanalFiltrados" :key="p.idProyecto" @mousedown.prevent="seleccionarProyectoCanal(p)">
                      <i class="bi bi-folder2 text-primary"></i><span>{{ p.orden }} · {{ p.descripcion }}</span>
                    </li>
                  </ul>
                </div>
                <select v-else id="canalProyecto" v-model="canalForm.idProyecto" class="form-select" required>
                  <option value="">Seleccionar proyecto...</option>
                  <option v-for="proyecto in proyectos" :key="proyecto.idProyecto" :value="proyecto.idProyecto">
                    {{ proyecto.orden }} · {{ proyecto.descripcion }}
                  </option>
                </select>
              </div>
              <div class="col-12">
                <label for="canalDescripcion" class="qh-label">
                  <i class="bi bi-card-text"></i> Descripción
                </label>
                <input id="canalDescripcion" v-model.trim="canalForm.descripcion" maxlength="150" class="form-control" placeholder="Ej. Mayorista" required>
              </div>
            </div>

            <div class="form-actions-row">
              <button v-if="canalEditando" class="btn qh-btn qh-btn-secondary" type="button" @click="cancelarCanal">
                <i class="bi bi-x-circle"></i> Cancelar
              </button>
              <button class="btn qh-btn qh-btn-primary" type="submit" :disabled="guardandoCanal || !canalValido">
                <span v-if="guardandoCanal" class="spinner-border spinner-border-sm"></span>
                <i v-else class="bi bi-check2-circle"></i>
                <span>{{ canalEditando ? 'Actualizar canal' : 'Guardar canal' }}</span>
              </button>
            </div>
          </form>
        </div>
      </article>
    </div>

    <!-- Tablas de Registros (Fila de 2 Tarjetas) -->
    <div class="workspace-grid lists-grid">
      <!-- Lista: Proyectos -->
      <article class="qh-panel list-card">
        <div class="qh-panel-header section-header list-header">
          <div class="list-title-wrap">
            <div class="section-icon mini"><i class="bi bi-list-task"></i></div>
            <div>
              <h3 class="section-title">Proyectos registrados</h3>
              <p class="section-text">{{ proyectosFiltrados.length }} resultado(s)</p>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <div class="page-size-control">
              <span>Mostrar</span>
              <select v-model.number="itemsPorPaginaProyecto" class="form-select form-select-sm">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
              </select>
              <span>registros</span>
            </div>
            <div class="search-box compact-search">
              <i class="bi bi-search"></i>
              <input v-model="buscarProyecto" placeholder="Buscar proyecto...">
              <button v-if="buscarProyecto" type="button" class="search-clear-btn" @click="buscarProyecto = ''">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="qh-panel-body table-body">
          <div class="table-responsive hierarchy-table">
            <table class="table table-hover align-middle mb-0 qh-admin-table">
              <thead>
                <tr>
                  <th style="width: 80px;">Orden</th>
                  <th>Descripción</th>
                  <th style="width: 90px; text-align: center;">Canales</th>
                  <th class="actions-col" style="width: 100px; text-align: center;">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!proyectosFiltrados.length">
                  <td colspan="4">
                    <div class="empty-state">
                      <i class="bi bi-folder2-open"></i>
                      <span>No hay proyectos registrados.</span>
                    </div>
                  </td>
                </tr>
                <tr v-for="proyecto in proyectosPaginados" :key="proyecto.idProyecto">
                  <td><span class="code-badge">{{ proyecto.orden }}</span></td>
                  <td class="fw-semibold text-dark">{{ proyecto.descripcion }}</td>
                  <td class="text-center"><span class="count-badge">{{ contarCanales(proyecto.idProyecto) }}</span></td>
                  <td class="text-center">
                    <div class="row-actions">
                      <button title="Editar proyecto" @click="editarProyecto(proyecto)"><i class="bi bi-pencil"></i></button>
                      <button class="danger" title="Eliminar proyecto" @click="eliminarProyecto(proyecto)"><i class="bi bi-trash3"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="proyectosFiltrados.length" class="table-bottom justify-content-end">
            <div class="pagination-compact">
              <button type="button" @click="paginaProyectos = 1" :disabled="paginaProyectos === 1" title="Primera página">
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button type="button" @click="paginaProyectos--" :disabled="paginaProyectos === 1" title="Anterior">
                <i class="bi bi-chevron-left"></i>
              </button>
              <span>Página {{ paginaProyectos }} de {{ totalPaginasProyectos }}</span>
              <button type="button" @click="paginaProyectos++" :disabled="paginaProyectos >= totalPaginasProyectos" title="Siguiente">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button type="button" @click="paginaProyectos = totalPaginasProyectos" :disabled="paginaProyectos >= totalPaginasProyectos" title="Última página">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>
      </article>

      <!-- Lista: Canales -->
      <article class="qh-panel list-card">
        <div class="qh-panel-header section-header list-header">
          <div class="list-title-wrap">
            <div class="section-icon mini"><i class="bi bi-kanban"></i></div>
            <div>
              <h3 class="section-title">Canales registrados</h3>
              <p class="section-text">{{ canalesFiltrados.length }} resultado(s)</p>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <div class="page-size-control">
              <span>Mostrar</span>
              <select v-model.number="itemsPorPaginaCanal" class="form-select form-select-sm">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
              </select>
              <span>registros</span>
            </div>
            <div class="search-box compact-search">
              <i class="bi bi-search"></i>
              <input v-model="buscarCanal" placeholder="Buscar canal...">
              <button v-if="buscarCanal" type="button" class="search-clear-btn" @click="buscarCanal = ''">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="qh-panel-body table-body">
          <div class="table-responsive hierarchy-table">
            <table class="table table-hover align-middle mb-0 qh-admin-table">
              <thead>
                <tr>
                  <th style="width: 80px;">Orden</th>
                  <th>Descripción</th>
                  <th>Proyecto</th>
                  <th class="actions-col" style="width: 100px; text-align: center;">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!canalesFiltrados.length">
                  <td colspan="4">
                    <div class="empty-state">
                      <i class="bi bi-signpost-split"></i>
                      <span>No hay canales registrados.</span>
                    </div>
                  </td>
                </tr>
                <tr v-for="canal in canalesPaginados" :key="canal.idCanal">
                  <td><span class="code-badge">{{ canal.orden }}</span></td>
                  <td class="fw-semibold text-dark">{{ canal.descripcion }}</td>
                  <td class="text-muted small"><i class="bi bi-folder2 text-primary me-1"></i>{{ proyectoNombre(canal.idProyecto) }}</td>
                  <td class="text-center">
                    <div class="row-actions">
                      <button title="Editar canal" @click="editarCanal(canal)"><i class="bi bi-pencil"></i></button>
                      <button class="danger" title="Eliminar canal" @click="eliminarCanal(canal)"><i class="bi bi-trash3"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="canalesFiltrados.length" class="table-bottom justify-content-end">
            <div class="pagination-compact">
              <button type="button" @click="paginaCanales = 1" :disabled="paginaCanales === 1" title="Primera página">
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button type="button" @click="paginaCanales--" :disabled="paginaCanales === 1" title="Anterior">
                <i class="bi bi-chevron-left"></i>
              </button>
              <span>Página {{ paginaCanales }} de {{ totalPaginasCanales }}</span>
              <button type="button" @click="paginaCanales++" :disabled="paginaCanales >= totalPaginasCanales" title="Siguiente">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button type="button" @click="paginaCanales = totalPaginasCanales" :disabled="paginaCanales >= totalPaginasCanales" title="Última página">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

const APIS = {
  listarProyectos: "BIClientes/ListarProyectos",
  listarSubProyectos: "BIClientes/ListarSubProyectos",
  guardarProyecto: "BIClientes/RegistrarProyecto",
  modificarProyecto: "BIClientes/ModificarProyecto",
  guardarSubProyecto: "BIClientes/RegistrarSubProyecto",
  modificarSubProyecto: "BIClientes/ModificarSubProyecto",
  eliminarProyecto: "BIClientes/EliminarProyecto",
  eliminarCanal: "BIClientes/EliminarCanal",
};

export default {
  name: "ClienteJerarquia",
  data() {
    return {
      proyectos: [],
      canales: [],
      proyectoForm: { orden: "", descripcion: "" },
      canalForm: { orden: "", descripcion: "", idProyecto: "" },
      proyectoEditando: "",
      canalEditando: "",
      buscarProyecto: "",
      buscarCanal: "",
      busquedaProyectoCanal: "",
      mostrarListaProyectosCanal: false,
      paginaProyectos: 1,
      paginaCanales: 1,
      itemsPorPaginaProyecto: 5,
      itemsPorPaginaCanal: 5,
      guardandoProyecto: false,
      guardandoCanal: false,
    };
  },
  watch: {
    buscarProyecto() { this.paginaProyectos = 1; },
    buscarCanal() { this.paginaCanales = 1; },
    itemsPorPaginaProyecto() { this.paginaProyectos = 1; },
    itemsPorPaginaCanal() { this.paginaCanales = 1; },
  },
  computed: {
    proyectosParaCanalFiltrados() {
      const q = this.normalizar(this.busquedaProyectoCanal);
      return this.proyectos.filter(
        p => !q || this.normalizar(`${p.orden} ${p.descripcion}`).includes(q)
      );
    },
    proyectoValido() {
      return (
        this.proyectoForm.orden !== "" &&
        this.proyectoForm.orden !== null &&
        !!String(this.proyectoForm.descripcion).trim()
      );
    },
    canalValido() {
      return (
        this.canalForm.orden !== "" &&
        this.canalForm.orden !== null &&
        !!String(this.canalForm.descripcion).trim() &&
        !!this.canalForm.idProyecto
      );
    },
    proyectosFiltrados() {
      const q = this.normalizar(this.buscarProyecto);
      return this.proyectos.filter(
        x => !q || this.normalizar(`${x.orden} ${x.descripcion}`).includes(q)
      );
    },
    totalPaginasProyectos() {
      return Math.ceil(this.proyectosFiltrados.length / this.itemsPorPaginaProyecto) || 1;
    },
    proyectosPaginados() {
      const start = (this.paginaProyectos - 1) * this.itemsPorPaginaProyecto;
      return this.proyectosFiltrados.slice(start, start + this.itemsPorPaginaProyecto);
    },
    canalesFiltrados() {
      const q = this.normalizar(this.buscarCanal);
      return this.canales.filter(
        x =>
          !q ||
          this.normalizar(
            `${x.orden} ${x.descripcion} ${this.proyectoNombre(x.idProyecto)}`
          ).includes(q)
      );
    },
    totalPaginasCanales() {
      return Math.ceil(this.canalesFiltrados.length / this.itemsPorPaginaCanal) || 1;
    },
    canalesPaginados() {
      const start = (this.paginaCanales - 1) * this.itemsPorPaginaCanal;
      return this.canalesFiltrados.slice(start, start + this.itemsPorPaginaCanal);
    },
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    normalizar(valor) {
      return String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    emitirResumen() {
      this.$emit("summary", {
        proyectos: this.proyectos.length,
        canales: this.canales.length,
      });
    },
    cargarDatos() {
      Promise.all([
        this.axios.get(APIS.listarProyectos),
        this.axios.get(APIS.listarSubProyectos),
      ])
        .then(([rp, rc]) => {
          const parse = r => Array.isArray(r?.data) ? r.data : (r?.data?.datos || []);

          this.proyectos = parse(rp).map(p => ({
            ...p,
            id: p.id,
            idProyecto: p.id,
            orden: p.nrOrden ?? "",
            descripcion: p.pryDescripcion || "",
          }));

          this.canales = parse(rc).map(c => ({
            ...c,
            id: c.id ?? c.idSubPry ?? c.idCanal ?? c.idSubProyecto,
            idCanal: c.id ?? c.idSubPry ?? c.idCanal ?? c.idSubProyecto,
            orden: c.nrOrden ?? "",
            idProyecto: c.idProyecto,
            descripcion: c.subPryDescripcion || "",
          }));

          this.emitirResumen();
          this.$emit("updated");
        })
        .catch(() => {
          this.proyectos = [];
          this.canales = [];
          this.emitirResumen();
          this.$emit("updated");
        });
    },
    guardarItem({ flag, endpoint, payload, tipo, onExito, method = "post", accion = "guardado" }) {
      this[flag] = true;
      const request = method === "put" ? this.axios.put(endpoint, payload) : this.axios.post(endpoint, payload);
      request
        .then(res => {
          const data = res?.data || {};
          if (data.exito === false) {
            return Swal.fire("Atención", data.mensaje || `No se pudo procesar el ${tipo.toLowerCase()}.`, "warning");
          }
          Swal.fire({
            title: `${tipo} ${accion}`,
            text: data.mensaje || `Registro ${accion} correctamente.`,
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          onExito();
          this.cargarDatos();
        })
        .catch(this.mostrarError)
        .finally(() => {
          this[flag] = false;
        });
    },
    guardarProyecto() {
      if (!this.proyectoValido) return;

      if (this.proyectoEditando) {
        this.guardarItem({
          flag: "guardandoProyecto",
          endpoint: APIS.modificarProyecto,
          method: "put",
          payload: {
            id: Number(this.proyectoEditando),
            Id: Number(this.proyectoEditando),
            nrOrden: Number(this.proyectoForm.orden),
            NrOrden: Number(this.proyectoForm.orden),
            descripcion: this.proyectoForm.descripcion,
            Descripcion: this.proyectoForm.descripcion,
          },
          tipo: "Proyecto",
          accion: "actualizado",
          onExito: () => this.cancelarProyecto(),
        });
      } else {
        this.guardarItem({
          flag: "guardandoProyecto",
          endpoint: APIS.guardarProyecto,
          method: "post",
          payload: {
            nrOrden: this.proyectoForm.orden,
            descripcion: this.proyectoForm.descripcion,
            NrOrden: this.proyectoForm.orden,
            Descripcion: this.proyectoForm.descripcion,
          },
          tipo: "Proyecto",
          accion: "guardado",
          onExito: () => this.cancelarProyecto(),
        });
      }
    },
    guardarCanal() {
      if (!this.canalValido) return;

      if (this.canalEditando) {
        this.guardarItem({
          flag: "guardandoCanal",
          endpoint: APIS.modificarSubProyecto,
          method: "put",
          payload: {
            id: Number(this.canalEditando),
            Id: Number(this.canalEditando),
            nrOrden: Number(this.canalForm.orden),
            NrOrden: Number(this.canalForm.orden),
            idProyecto: Number(this.canalForm.idProyecto),
            IdProyecto: Number(this.canalForm.idProyecto),
            subPrylDescripcion: this.canalForm.descripcion,
            SubPrylDescripcion: this.canalForm.descripcion,
            descripcion: this.canalForm.descripcion,
            Descripcion: this.canalForm.descripcion,
          },
          tipo: "Canal",
          accion: "actualizado",
          onExito: () => this.cancelarCanal(),
        });
      } else {
        this.guardarItem({
          flag: "guardandoCanal",
          endpoint: APIS.guardarSubProyecto,
          method: "post",
          payload: {
            nrOrden: this.canalForm.orden,
            NrOrden: this.canalForm.orden,
            idProyecto: this.canalForm.idProyecto,
            IdProyecto: this.canalForm.idProyecto,
            subPrylDescripcion: this.canalForm.descripcion,
            SubPrylDescripcion: this.canalForm.descripcion,
            descripcion: this.canalForm.descripcion,
          },
          tipo: "Canal",
          accion: "guardado",
          onExito: () => this.cancelarCanal(),
        });
      }
    },
    seleccionarProyectoCanal(p) {
      this.canalForm.idProyecto = p.idProyecto;
      this.busquedaProyectoCanal = `${p.orden} · ${p.descripcion}`;
      this.mostrarListaProyectosCanal = false;
    },
    ocultarListaProyectosCanal() {
      setTimeout(() => {
        this.mostrarListaProyectosCanal = false;
      }, 200);
    },
    limpiarProyectoCanal() {
      this.canalForm.idProyecto = "";
      this.busquedaProyectoCanal = "";
    },
    editarProyecto(x) {
      this.proyectoEditando = x.id;
      this.proyectoForm = { orden: x.orden, descripcion: x.descripcion };
    },
    editarCanal(x) {
      this.canalEditando = x.id ?? x.idSubPry ?? x.idCanal ?? x.idSubProyecto;
      this.canalForm = {
        orden: x.orden,
        descripcion: x.descripcion,
        idProyecto: x.idProyecto,
      };
      const p = this.proyectos.find(item => String(item.id) === String(x.idProyecto));
      this.busquedaProyectoCanal = p ? `${p.orden} · ${p.descripcion}` : x.idProyecto;
    },
    cancelarProyecto() {
      this.proyectoEditando = "";
      this.proyectoForm = { orden: "", descripcion: "" };
    },
    cancelarCanal() {
      this.canalEditando = "";
      this.canalForm = { orden: "", descripcion: "", idProyecto: "" };
      this.busquedaProyectoCanal = "";
    },
    eliminarItem({ tipo, endpoint, item, checkFn }) {
      if (checkFn) {
        const errorMsg = checkFn();
        if (errorMsg) return Swal.fire("No se puede eliminar", errorMsg, "warning");
      }

      Swal.fire({
        title: `¿Eliminar ${tipo}?`,
        text: `Orden ${item.orden} · ${item.descripcion}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      }).then(r => {
        if (r.isConfirmed) {
          this.axios
            .delete(endpoint, { params: { id: item.id } })
            .then(res => {
              const data = res?.data;
              const msg = typeof data === "string" ? data : (data?.mensaje || data?.message || "Registro eliminado.");

              if (typeof data === "string" && data.toLowerCase().includes("error")) {
                return Swal.fire("Atención", msg, "error");
              }

              Swal.fire({
                title: `${tipo.charAt(0).toUpperCase() + tipo.slice(1)} eliminado`,
                text: msg,
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
              });
              this.cargarDatos();
            })
            .catch(this.mostrarError);
        }
      });
    },
    eliminarProyecto(x) {
      this.eliminarItem({
        tipo: "proyecto",
        endpoint: APIS.eliminarProyecto,
        item: x,
        checkFn: () => {
          const asociados = this.contarCanales(x.id);
          return asociados ? `El proyecto tiene ${asociados} canal(es) asociado(s).` : null;
        },
      });
    },
    eliminarCanal(x) {
      this.eliminarItem({
        tipo: "canal",
        endpoint: APIS.eliminarCanal,
        item: x,
      });
    },
    contarCanales(idProyecto) {
      return this.canales.filter(
        x => String(x.idProyecto) === String(idProyecto)
      ).length;
    },
    proyectoNombre(idProyecto) {
      const p = this.proyectos.find(
        x => String(x.id) === String(idProyecto)
      );
      return p ? `${p.orden} · ${p.descripcion}` : idProyecto;
    },
    mostrarError(error) {
      const msg =
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        error?.message ||
        "Verifica la información e inténtalo nuevamente.";
      Swal.fire("No se pudo completar la operación", msg, "error");
    },
  },
};
</script>

<style scoped>
.qh-client-page {
  display: grid;
  gap: 1.15rem;
}

/* Panel Header & Hero */
.hero-panel {
  overflow: hidden;
  border-radius: 18px;
}

.hero-header {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(290px, 0.8fr);
  gap: 1.5rem;
  align-items: center;
  padding: 1.25rem 1.5rem;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.section-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  width: fit-content;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  background: #eef6ff;
  color: var(--qhatu-primary, #0b5db7);
  font-weight: 800;
  font-size: 0.76rem;
  letter-spacing: 0.02em;
}

.hero-title {
  margin: 0;
  font-size: clamp(1.6rem, 2.8vw, 2.2rem);
  font-weight: 800;
  color: #172a45;
}

.hero-subtitle {
  margin: 0;
  color: var(--qhatu-muted, #5f748d);
  font-size: 0.92rem;
  line-height: 1.45;
}

/* Hero Flow Box */
.hero-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  background: linear-gradient(145deg, #ffffff, #f4f8fd);
  border: 1px solid #dce7f3;
  box-shadow: 0 8px 20px rgba(15, 35, 60, 0.04);
}

.flow-card {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.8rem 0.9rem;
  border-radius: 14px;
  border: 1px solid #d9e6f2;
  background: #ffffff;
  text-align: center;
  box-shadow: 0 6px 16px rgba(13, 38, 76, 0.04);
}

.flow-card.secondary {
  background: #f7fafe;
}

.flow-level {
  color: var(--qhatu-primary, #0b5db7);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.flow-name {
  color: #1c2d42;
  font-size: 1rem;
  font-weight: 800;
}

.flow-desc {
  color: var(--qhatu-muted, #647890);
  font-size: 0.72rem;
}

.flow-arrow {
  color: #7b92ab;
  font-size: 1.35rem;
}

/* Grids & Cards */
.workspace-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.15rem;
}

.workspace-card,
.list-card {
  border-radius: 18px;
  border: 1px solid var(--qhatu-border, #dbe5f0);
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 35, 60, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--qhatu-border, #e3ecf5);
  background: #fafcff;
}

.section-icon {
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #eef6ff;
  color: var(--qhatu-primary, #0b5db7);
  font-size: 1.3rem;
}

.section-icon.mini {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 1.15rem;
}

.section-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a2b40;
}

.section-text {
  margin: 0.15rem 0 0;
  color: var(--qhatu-muted, #61768e);
  font-size: 0.82rem;
}

/* Form Inputs & Labels */
.workspace-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.qh-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #24364e;
}

.qh-label i {
  color: #63778e;
  font-size: 0.9rem;
}

.workspace-form .form-control,
.workspace-form .form-select {
  min-height: 46px;
  border-radius: 12px;
  border: 1px solid #d7e3f0;
  font-size: 0.9rem;
  color: #1e2f45;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.workspace-form .form-control:focus,
.workspace-form .form-select:focus {
  border-color: #0b5db7;
  box-shadow: 0 0 0 3px rgba(11, 93, 183, 0.12);
}

/* Buttons */
.form-actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
  padding-top: 0.5rem;
}

.qh-btn {
  min-height: 46px;
  padding: 0.6rem 1.35rem;
  border-radius: 12px;
  font-weight: 750;
  font-size: 0.88rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.qh-btn-primary {
  border: 0;
  color: #ffffff;
  background: linear-gradient(135deg, #0759b7, #0d73d6);
  box-shadow: 0 6px 18px rgba(7, 89, 183, 0.22);
}

.qh-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(7, 89, 183, 0.3);
  color: #ffffff;
}

.qh-btn-primary:disabled {
  background: #d4e2f2;
  color: #7b8fa7;
  box-shadow: none;
  cursor: not-allowed;
}

.qh-btn-secondary {
  border: 1px solid #d3e0ed;
  background: #ffffff;
  color: #4b5f76;
}

.qh-btn-secondary:hover {
  background: #f2f7fc;
  color: #1f3148;
}

/* Lists & Search */
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.list-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-box.compact-search {
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.8rem;
  border: 1px solid #d5e2f0;
  border-radius: 12px;
  background: #ffffff;
  transition: border-color 0.2s;
}

.search-box.compact-search:focus-within {
  border-color: #0b5db7;
}

.search-box.compact-search i {
  color: #7c90a6;
}

.search-box.compact-search input {
  flex: 1;
  min-width: 0;
  min-height: 40px;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 0.85rem;
  color: #1e2f45;
}

.search-clear-btn {
  border: 0;
  background: transparent;
  color: #92a4b8;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.search-clear-btn:hover {
  color: #40556e;
}

/* Tables */
.table-body {
  padding: 0;
}

.hierarchy-table {
  max-height: 380px;
  overflow-y: auto;
}

.qh-admin-table {
  margin: 0;
}

.qh-admin-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f1f6fb;
  color: #334a63;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e1ecf6;
}

.qh-admin-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #edf3f9;
  font-size: 0.85rem;
}

.code-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.6rem;
  border-radius: 8px;
  background: #edf5fc;
  color: #0b5db7;
  font-weight: 800;
  font-size: 0.82rem;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #eef4f9;
  color: #4f637a;
  font-weight: 800;
  font-size: 0.78rem;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.row-actions button {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #fff5da;
  color: #b37300;
  display: grid;
  place-items: center;
  transition: transform 0.15s, background-color 0.15s;
}

.row-actions button:hover {
  transform: scale(1.06);
  background: #ffebbc;
}

.row-actions button.danger {
  background: #ffeded;
  color: #d93848;
}

.row-actions button.danger:hover {
  background: #ffd9dc;
}

.empty-state {
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #7b8ea5;
  padding: 2rem;
}

.empty-state i {
  font-size: 2rem;
  color: #a4b5c8;
}

/* Breakpoints & Responsiveness */
@media (max-width: 1199.98px) {
  .hero-header {
    grid-template-columns: 1fr;
  }
  .workspace-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {
  .hero-flow {
    flex-direction: column;
  }
  .flow-connector {
    transform: rotate(90deg);
  }
  .flow-card {
    width: 100%;
  }
  .list-header {
    flex-direction: column;
    align-items: stretch;
  }
  .search-box.compact-search {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .form-actions-row {
    flex-direction: column;
    align-items: stretch;
  }
  .qh-btn {
    width: 100%;
  }
}

/* Footer de Paginación Compacto */
.table-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-top: 1px solid var(--qhatu-border, #edf1f5);
  background: #fdfefe;
}

.pagination-info {
  font-size: 0.76rem;
  color: var(--qhatu-muted, #6d7d90);
  font-weight: 600;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.pag-btn {
  width: 32px;
  height: 32px;
  border: 1px solid var(--qhatu-border, #dce5ef);
  border-radius: 8px;
  background: #ffffff;
  color: var(--qhatu-primary, #087bb8);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.pag-btn:hover:not(:disabled) {
  background: var(--qhatu-primary-soft, #eaf5fc);
  border-color: var(--qhatu-primary, #087bb8);
}

.pag-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
